import logger from './logger'
// Global state to prevent concurrent refresh attempts
let isRefreshing = false
let refreshPromise = null

/**
* @description Executes IAP session refresh using a popup window and verifies success via polling.
* @returns {Promise<void>} A promise that resolves only when the session is confirmed active.
*/
function executeIapRefreshFlow () {
  const functionName = 'executeIapRefreshFlow'

  if (isRefreshing) {
    logger.info('IAP refresh already in progress. Reusing existing promise.', {
      functionName
    })
    return refreshPromise
  }

  isRefreshing = true
  // Tell React that refresh has started
  window.dispatchEvent(new Event('iap-refresh-start'))
  logger.info('Starting IAP refresh flow using popup.', { functionName })
  const securePath = '/api/auth/health'

  refreshPromise = new Promise((resolve, reject) => {
    const TOTAL_TIMEOUT_MS = 60000

    let poller
    let popup

    const cleanup = () => {
      clearInterval(poller)
      clearTimeout(totalTimeoutTimer)
      if (popup && !popup.closed) popup.close()
      isRefreshing = false
      refreshPromise = null
      // Tell React that refresh is finished (success or fail)
      window.dispatchEvent(new Event('iap-refresh-end'))
    }

    const checkSession = async () => {
      try {
        const response = await fetch(securePath, {
          method: 'GET',
          credentials: 'include',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          cache: 'no-store'
        })

        if (response.status !== 401) {
          logger.info('Polling successful. IAP session is now active.', {
            functionName: 'checkSession'
          })
          cleanup()
          resolve()
        }
      } catch (error) {
        logger.warn('Polling request failed, will retry.', {
          functionName: 'checkSession',
          originalError: error
        })
      }
    }

    const totalTimeoutTimer = setTimeout(() => {
      logger.error('IAP session refresh timed out.', {
        functionName,
        context: { timeout: TOTAL_TIMEOUT_MS }
      })
      cleanup()
      reject(
        new Error(
          `IAP session refresh timed out after ${TOTAL_TIMEOUT_MS /
            1000} seconds.`
        )
      )
    }, TOTAL_TIMEOUT_MS)

    // This is more reliable than a fixed large number like 9999, as it adapts to any screen size.
    const offScreenLeft = window.screen.width + 100
    const offScreenTop = window.screen.height + 100

    // Use the smallest possible size and the calculated off-screen position.
    const popupFeatures = `width=1,height=1,right=${offScreenLeft},bottom=${offScreenTop}`

    popup = window.open(
      `${securePath}?gcp-iap-mode=DO_SESSION_REFRESH`,
      'iap_refresh',
      popupFeatures
    )

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      logger.error('Popup window for session refresh was blocked.', {
        functionName
      })
      cleanup()
      return reject(
        new Error(
          'Popup window was blocked. Please allow popups for this site.'
        )
      )
    }

    logger.info('Popup opened. Starting polling to verify session.', {
      functionName
    })
    poller = setInterval(checkSession, 2000)
  })

  return refreshPromise
}

/**
* @description A wrapper for fetch that automatically handles IAP 401 errors using the popup flow.
* @param {string} url - The API endpoint to call.
* @param {object} options - The options object for the fetch call.
* @param {boolean} [isRetry=false] - Internal flag to prevent infinite retry loops.
* @returns {Promise<Response>} A promise that resolves with the final API response.
*/
export async function fetchWithIap (url, options = {}, isRetry = false) {
  const functionName = 'fetchWithIap'
  const finalOptions = {
    ...options,
    headers: { ...options.headers, 'X-Requested-With': 'XMLHttpRequest' }
  }

  const response = await fetch(url, finalOptions)

  // Centralized 504 Gateway Timeout Handling
  if (response.status === 504) {
    console.error(`API call to $ timed out (504).`)
    // Create a new error object
    const error = new Error(
      'Request Timed Out: The server is taking too long to respond. Please try again.'
    )
    // Attach the status code to the error object
    error.status = 504
    // Throw the enhanced error
    throw error
  }

  if (response.status === 401) {
    if (isRetry) {
      const criticalError = new Error(
        'IAP session refresh failed even after a retry.'
      )
      logger.error(criticalError.message, { functionName, context: { url } })
      throw criticalError
    }

    logger.warn('IAP session expired. Attempting refresh via popup.', {
      functionName,
      context: { url }
    })
    try {
      await executeIapRefreshFlow()
      logger.info(
        'Session refresh seems successful. Retrying original API call.',
        { functionName, context: { url } }
      )
      return fetchWithIap(url, options, true)
    } catch (refreshError) {
      logger.error('The IAP refresh flow failed.', {
        functionName,
        originalError: refreshError,
        context: { url }
      })
      throw refreshError
    }
  }

  return response
}
